"use client";

import React from "react";
import { useField } from "formik";
import { AlertCircle } from "lucide-react";

interface TextAreaFieldProps {
  label: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  rows?: number;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  variant?: "light" | "dark";
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  label,
  name,
  placeholder,
  required = false,
  rows = 4,
  disabled = false,
  className = "",
  inputClassName = "",
  labelClassName = "",
  variant = "light",
}) => {
  const [field, meta] = useField(name);
  const isError = meta.touched && meta.error;

  const isDark = variant === "dark";

  const defaultInputStyles = isError
    ? `border-red-200 ${isDark ? "bg-red-500/10" : "bg-red-50/10"} focus:border-red-400 focus:ring-1 focus:ring-red-50 text-red-900 placeholder:text-red-300`
    : isDark
      ? "border-border-1 bg-black-3 text-text-1 focus:border-gold-dim placeholder:text-text-3"
      : "border-slate-200 bg-white focus:border-gold-dim focus:ring-4 focus:ring-gold-dim/10 text-slate-700 placeholder:text-slate-400 shadow-sm hover:border-slate-300";

  const defaultLabelStyles = isDark
    ? "text-[10px] text-text-3 font-bold tracking-widest uppercase flex items-center gap-2"
    : "text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold";

  return (
    <div className={`space-y-1.5 flex flex-col h-full ${className}`}>
      <div className="flex items-center justify-between">
        <label htmlFor={name} className={labelClassName || defaultLabelStyles}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      </div>
      <textarea
        {...field}
        id={name}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none resize-none flex-1 
          ${inputClassName || defaultInputStyles} 
          ${disabled ? "opacity-50 cursor-not-allowed bg-slate-50" : ""}`}
      />
      {isError && (
        <div className="flex items-center gap-1 text-[10px] text-red-500 font-bold animate-in fade-in slide-in-from-right-1">
          <AlertCircle size={10} />
          <span>{meta.error}</span>
        </div>
      )}
    </div>
  );
};
